import React, {
    useState, useEffect, useCallback, useRef
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Flex } from 'components/ui';
import Container from 'components/Container/Container';
import {
    colors, space, mediaQueries, zIndices, site, fontSizes, fontWeights, lineHeights
} from 'style/config';
import Chevron from 'components/Chevron';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import localeUtils from 'utils/LanguageLocale';
import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import StickyNavBarEN from 'components/ProductPage/StickyNavBar/locales/StickyNavBar_en_US';
import StickyNavBarFR from 'components/ProductPage/StickyNavBar/locales/StickyNavBar_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(StickyNavBarEN, StickyNavBarFR);

const TOP_SECTION = { id: 'top', locKey: 'top', targetId: null };

const NAV_SECTIONS = [
    { id: 'details', locKey: 'details', targetId: 'details' },
    { id: 'similar', locKey: 'similar', targetId: 'similar' },
    { id: 'qanda', locKey: 'qAndA', targetId: 'QandA' },
    { id: 'reviews', locKey: 'reviews', targetId: 'ratings-reviews-container' }
];

const PRODUCT_MEDIA_ID = 'product-media';
const NAV_HEIGHT = 44;
const SCROLL_OFFSET = 10;
const VISIBILITY_FALLBACK_THRESHOLD = 400;

/**
 * Sticky navigation bar rendered on the PDP for A/B test #3747 (STICKY_NAV_BAR_PDP).
 * Becomes visible once the product image has scrolled above the viewport top. Provides
 * jump links to PDP sections (Details, Similar, Q&A, Reviews) plus a scroll-to-top
 * button. Challenger 2 also renders the product image + brand + name on the right.
 * @param {Object} props
 * @param {{ challengerOne: boolean, challengerTwo: boolean }} props.experience - AB test experience flags.
 * @param {string} props.productName - Display name of the current product.
 * @param {string} props.brandName - Brand display name of the current product.
 * @param {Object} props.focusedSku - Focused sku object (used for the Challenger 2 thumbnail).
 */
function StickyNavBar({ experience, productName, brandName, focusedSku }) {
    const [isVisible, setIsVisible] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const rafId = useRef(null);
    const barRef = useRef(null);

    const updateScrollState = useCallback(() => {
        const productMedia = document.getElementById(PRODUCT_MEDIA_ID);
        const scrolledPastMedia = productMedia ? productMedia.getBoundingClientRect().bottom <= 0 : window.scrollY > VISIBILITY_FALLBACK_THRESHOLD;

        setIsVisible(scrolledPastMedia);

        const barBottom = barRef.current ? barRef.current.getBoundingClientRect().bottom : NAV_HEIGHT;
        const activeThreshold = barBottom + space[4];
        let current = null;

        for (let i = NAV_SECTIONS.length - 1; i >= 0; i--) {
            const section = NAV_SECTIONS[i];
            const el = document.getElementById(section.targetId);

            if (el && el.getBoundingClientRect().top <= activeThreshold) {
                current = section.id;

                break;
            }
        }

        setActiveSection(current);
    }, []);

    const handleScroll = useCallback(() => {
        if (rafId.current) {
            return;
        }

        rafId.current = requestAnimationFrame(() => {
            rafId.current = null;
            updateScrollState();
        });
    }, [updateScrollState]);

    useEffect(() => {
        updateScrollState();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);

            if (rafId.current) {
                cancelAnimationFrame(rafId.current);
            }
        };
    }, [handleScroll, updateScrollState]);

    if (!isVisible) {
        return null;
    }

    const scrollTo = (targetId, sectionId) => {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                actionInfo: `sticky_nav:${sectionId}`,
                linkName: 'D=c55'
            }
        });

        if (!targetId) {
            window.scrollTo({ top: 0, behavior: 'smooth' });

            return;
        }

        const el = document.getElementById(targetId);

        if (el) {
            const barBottom = barRef.current ? barRef.current.getBoundingClientRect().bottom : NAV_HEIGHT;
            const top = el.getBoundingClientRect().top + window.scrollY - barBottom - SCROLL_OFFSET;

            window.scrollTo({ top, behavior: 'smooth' });
        }
    };

    return (
        <Box
            ref={barRef}
            position='fixed'
            left={0}
            right={0}
            zIndex={zIndices.fixedBar}
            backgroundColor='white'
            boxShadow='light'
            baseCss={styles.root}
            role='navigation'
            aria-label={getText('navLabel')}
        >
            <Container>
                <Flex
                    alignItems='center'
                    height={NAV_HEIGHT}
                    gap={5}
                >
                    <button
                        type='button'
                        onClick={() => scrollTo(TOP_SECTION.targetId, TOP_SECTION.id)}
                        css={styles.topButton}
                    >
                        <Chevron
                            direction='up'
                            size={fontSizes.sm}
                        />
                        <span>{getText(TOP_SECTION.locKey)}</span>
                    </button>
                    <Flex
                        alignItems='center'
                        gap={7}
                        baseCss={styles.navLinks}
                    >
                        {NAV_SECTIONS.map(section => {
                            const isActive = activeSection === section.id;

                            return (
                                <Text
                                    key={section.id}
                                    is='button'
                                    type='button'
                                    fontSize='base'
                                    lineHeight='18px'
                                    fontWeight={isActive ? 'bold' : 'normal'}
                                    color={colors.black}
                                    onClick={() => scrollTo(section.targetId, section.id)}
                                    baseCss={[styles.navLink, isActive && styles.activeLink]}
                                    aria-current={isActive ? 'true' : null}
                                    children={getText(section.locKey)}
                                />
                            );
                        })}
                    </Flex>
                    {experience?.challengerTwo && (
                        <Flex
                            alignItems='center'
                            gap={3}
                            baseCss={styles.productInfo}
                        >
                            {focusedSku?.skuImages && (
                                <ProductImage
                                    id={focusedSku.skuId}
                                    skuImages={focusedSku.skuImages}
                                    size={36}
                                    width={36}
                                    hideBadge={true}
                                    altText={productName}
                                    disableLazyLoad={true}
                                    css={styles.productImage}
                                />
                            )}
                            <Box baseCss={styles.productText}>
                                <span css={styles.brand}>{brandName}</span> {productName}
                            </Box>
                        </Flex>
                    )}
                </Flex>
            </Container>
        </Box>
    );
}

const styles = {
    root: {
        top: site.headerHeight,
        transition: 'transform 0.3s ease',
        [mediaQueries.sm]: {
            top: 0
        }
    },
    topButton: {
        background: colors.nearWhite,
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: space[1],
        padding: `0 ${space[3]}px`,
        height: '100%',
        whiteSpace: 'nowrap',
        fontSize: fontSizes.base,
        lineHeight: lineHeights.tight,
        color: colors.black,
        [mediaQueries.xsMax]: {
            marginLeft: -space.container,
            paddingRight: space[4]
        }
    },
    navLinks: {
        [mediaQueries.xsMax]: {
            // Figma SMUI spec (node 1:45) calls for 30px between nav links; off the space scale so kept as a raw value.
            gap: 30
        }
    },
    navLink: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '1px 0',
        borderBottom: '2px solid transparent',
        whiteSpace: 'nowrap',
        '&:hover': {
            borderBottomColor: colors.black
        }
    },
    activeLink: {
        borderBottomColor: colors.black
    },
    productInfo: {
        marginLeft: 'auto',
        minWidth: 0,
        flexShrink: 1,
        [mediaQueries.xsMax]: {
            display: 'none'
        }
    },
    productImage: {
        flexShrink: 0,
        '& img': {
            borderRadius: 2,
            objectFit: 'cover'
        }
    },
    productText: {
        fontSize: fontSizes.base,
        lineHeight: lineHeights.tight,
        color: colors.black,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    brand: {
        fontWeight: fontWeights.bold
    }
};

export default wrapFunctionalComponent(StickyNavBar, 'StickyNavBar');
